import { Doctor, PortalMode, User } from '../types/api';
import { mapStoredUserToDoctor, parseStoredUser } from './doctorUser';

export const DR_TELE_PORTAL_KEY = 'drTele_portalMode';
export const DR_TELE_SIG_GATE_KEY = 'drTele_signatureGate';

export function clearPersistedTeleSession(): void {
  localStorage.removeItem(DR_TELE_PORTAL_KEY);
  localStorage.removeItem(DR_TELE_SIG_GATE_KEY);
}

export function persistTelePortal(portal: PortalMode): void {
  localStorage.setItem(DR_TELE_PORTAL_KEY, portal);
}

/** Persist queue/signature gate string, or remove when null (doctor before signature step). */
export function persistTeleSignatureGate(gate: string | null): void {
  if (gate == null || gate === '') localStorage.removeItem(DR_TELE_SIG_GATE_KEY);
  else localStorage.setItem(DR_TELE_SIG_GATE_KEY, gate);
}

function parseJwtExpMs(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const p = JSON.parse(atob(parts[1])) as { exp?: number };
    return typeof p.exp === 'number' ? p.exp * 1000 : null;
  } catch {
    return null;
  }
}

/** True if access token exists and JWT `exp` is in the future (with skew). */
export function isAccessTokenValidInStorage(skewMs = 60_000): boolean {
  const token = localStorage.getItem('accessToken');
  if (!token) return false;
  const expMs = parseJwtExpMs(token);
  if (expMs == null) return true;
  return expMs > Date.now() + skewMs;
}

export function inferPortalFromUser(u: User): PortalMode {
  return u.roleId === 2 ? 'doctor' : 'staff';
}

export type RestoredTeleSession = {
  doctor: Doctor;
  portalMode: PortalMode;
  signatureULID: string | null;
};

/**
 * Rebuild in-memory routing state from localStorage after a full page load.
 * Access token must still be valid; otherwise clears auth storage.
 */
export function restoreTeleSessionFromStorage(): RestoredTeleSession | null {
  const token = localStorage.getItem('accessToken');
  if (!token) {
    clearPersistedTeleSession();
    return null;
  }
  if (!isAccessTokenValidInStorage()) {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    clearPersistedTeleSession();
    return null;
  }

  const u = parseStoredUser();
  if (!u) return null;

  const rawPortal = localStorage.getItem(DR_TELE_PORTAL_KEY) as PortalMode | null;
  const portalMode: PortalMode =
    rawPortal === 'doctor' || rawPortal === 'staff' ? rawPortal : inferPortalFromUser(u);

  const rawGate = localStorage.getItem(DR_TELE_SIG_GATE_KEY);
  let signatureULID: string | null = rawGate;

  if (portalMode === 'staff') {
    signatureULID = rawGate || 'staff-skip';
  } else if (portalMode === 'doctor' && !signatureULID && u.hasSignature) {
    signatureULID = `existing-sig-${u.id}`;
  }

  return {
    doctor: mapStoredUserToDoctor(u),
    portalMode,
    signatureULID,
  };
}
